"use client"

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useState } from 'react'

import { motion } from 'motion/react'

export function ContactForm() {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [contactMode, setContactMode] = useState('callback')

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsSubmitting(true)

        // Collect form data
        const formData = new FormData(e.target as HTMLFormElement);
        const data = Object.fromEntries(formData.entries());
        console.log("Form Submitted:", data);

        await new Promise((resolve) => setTimeout(resolve, 1000))
        setIsSubmitting(false)
        alert('Message sent successfully!')
    }

    return (
        <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
        >
            <form onSubmit={handleSubmit} className="space-y-8">

                {/* Contact Mode */}
                <fieldset className="space-y-4">
                    <legend className="text-sm font-bold text-foreground">Preferred Mode of Contact *</legend>
                    <div className="grid grid-cols-2 gap-4">
                        <label className={`
                            flex items-center justify-between p-4 rounded border cursor-pointer transition-all
                            ${contactMode === 'callback' ? 'border-[#C6A87C] bg-[#C6A87C]/5' : 'border-border hover:border-gray-400'}
                        `}>
                            <span className="text-sm font-medium">Request a call back</span>
                            <input
                                type="radio"
                                name="contactMode"
                                value="callback"
                                checked={contactMode === 'callback'}
                                onChange={(e) => setContactMode(e.target.value)}
                                className="accent-[#C6A87C] w-4 h-4"
                            />
                        </label>
                        <label className={`
                            flex items-center justify-between p-4 rounded border cursor-pointer transition-all
                            ${contactMode === 'video' ? 'border-[#C6A87C] bg-[#C6A87C]/5' : 'border-border hover:border-gray-400'}
                        `}>
                            <span className="text-sm font-medium">Schedule a video call</span>
                            <input
                                type="radio"
                                name="contactMode"
                                value="video"
                                checked={contactMode === 'video'}
                                onChange={(e) => setContactMode(e.target.value)}
                                className="accent-[#C6A87C] w-4 h-4"
                            />
                        </label>
                    </div>
                </fieldset>

                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <Input
                            id="firstName"
                            name="firstName"
                            required
                            placeholder="First Name *"
                            className="rounded h-12 border-gray-300 focus:border-[#C6A87C] focus:ring-0 placeholder:text-gray-400"
                        />
                    </div>
                    <div className="space-y-2">
                        <Input
                            id="lastName"
                            name="lastName"
                            required
                            placeholder="Last Name *"
                            className="rounded h-12 border-gray-300 focus:border-[#C6A87C] focus:ring-0 placeholder:text-gray-400"
                        />
                    </div>
                </div>

                {/* Contact Details */}
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="flex gap-2">
                        <div className="w-[100px] shrink-0"> {/* Country Code Placeholder */}
                            <div className="h-12 w-full rounded border border-gray-300 flex items-center px-3 bg-white text-sm text-gray-500">
                                🇦🇪 +971
                            </div>
                        </div>
                        <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            placeholder="XX XXX XXXX"
                            className="rounded h-12 border-gray-300 focus:border-[#C6A87C] focus:ring-0 placeholder:text-gray-400"
                        />
                    </div>
                    <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        placeholder="Email *"
                        className="rounded h-12 border-gray-300 focus:border-[#C6A87C] focus:ring-0 placeholder:text-gray-400"
                    />
                </div>

                {/* Dropdowns */}
                <div className="space-y-6">
                    <select name="propertyType" className="flex h-12 w-full items-center justify-between rounded border border-gray-300 bg-transparent px-3 py-2 text-sm placeholder:text-gray-400 focus:border-[#C6A87C] focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50 appearance-none bg-white text-gray-500">
                        <option value="" disabled selected>Property Type</option>
                        <option value="apartment">Apartment</option>
                        <option value="villa">Villa</option>
                        <option value="townhouse">Townhouse</option>
                    </select>

                    <div className="grid grid-cols-2 gap-6">
                        <select name="project" className="flex h-12 w-full items-center justify-between rounded border border-gray-300 bg-transparent px-3 py-2 text-sm placeholder:text-gray-400 focus:border-[#C6A87C] focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50 appearance-none bg-white text-gray-500">
                            <option value="" disabled selected>Project</option>
                            <option value="hartland">Sobha Hartland</option>
                            <option value="reserve">The Reserve</option>
                        </select>
                        <select name="unitType" className="flex h-12 w-full items-center justify-between rounded border border-gray-300 bg-transparent px-3 py-2 text-sm placeholder:text-gray-400 focus:border-[#C6A87C] focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50 appearance-none bg-white text-gray-500">
                            <option value="" disabled selected>Unit Type</option>
                            <option value="1bed">1 Bedroom</option>
                            <option value="2bed">2 Bedroom</option>
                            <option value="3bed">3 Bedroom</option>
                        </select>
                    </div>
                </div>

                {/* Checkboxes */}
                <div className="space-y-4 pt-2">
                    <label className="flex items-start gap-3 cursor-pointer group">
                        <input type="checkbox" name="terms" className="mt-1 w-4 h-4 rounded border-gray-300 text-[#C6A87C] focus:ring-[#C6A87C]" />
                        <span className="text-sm text-gray-600">
                            I've read and agree to the <a href="/privacy" className="underline hover:text-[#C6A87C]">privacy policy</a>. *
                        </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer group">
                        <input type="checkbox" name="marketing" className="mt-1 w-4 h-4 rounded border-gray-300 text-[#C6A87C] focus:ring-[#C6A87C]" />
                        <span className="text-sm text-gray-600">
                            I'd like to hear about news and offers.
                        </span>
                    </label>
                </div>

                <div className="pt-4">
                    <Button
                        type="submit"
                        variant="outline"
                        className="w-full md:w-auto rounded-xl px-8 h-10 uppercase tracking-widest text-xs font-bold border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Sending...' : 'Submit'}
                    </Button>
                </div>

                <p className="text-[10px] text-gray-400 mt-6">
                    This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of Service apply.
                </p>

            </form>
        </motion.div>
    )
}
